import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import * as React from 'react';
import styled from 'styled-components';
import { ErrorHandler } from '../../../../../../../components/ErrorHandler';
import { TextField } from '../../../../../../../components/TextField';
import { Field, ListItem } from '../../../../../../../design-system';
import { ResponsePostCommand } from '../../../../../../../utils/api';
import { ResetDialog } from '../../Settings/components/commandDialogs/ResetDialog';
import { CommandItem } from '../../Settings/components/CommandItem';

const StyledAlert = styled(Alert)`
  .MuiAlert-message {
    width: calc(100% - 16px - 22px);
  }
`;

type ConfigurationItemProps = {
  id: string;
  value?: string;
  isReadonly?: boolean;
  isDisabled: boolean;
  loading: boolean;
  error: unknown;
  response: ResponsePostCommand | undefined;
  update: (key: string, value: string) => void;
};

export const ConfigurationItem: React.FC<ConfigurationItemProps> = ({
  id,
  value = '',
  isReadonly,
  isDisabled,
  loading,
  error,
  response,
  update,
}: ConfigurationItemProps) => {
  const [configValue, setConfigValue] = React.useState(value);
  const [isResetDialogOpen, setResetDialogOpen] = React.useState(false);

  return (
    <ListItem>
      {error && (
        <Box mb={2}>
          <ErrorHandler description="Command Error" error={error} />
        </Box>
      )}
      {response && (
        <Box mb={2}>
          <StyledAlert severity="info">
            {response?.data.status === 'Accepted' ? (
              <CommandItem
                description="Charge point requires a hard reset to apply these changes."
                dialog={<ResetDialog isOpen={isResetDialogOpen} onClose={() => setResetDialogOpen(false)} />}
                isDisabled={false}
                label="Reboot charge point"
                onClick={() => setResetDialogOpen(true)}
              />
            ) : (
              <>
                <AlertTitle>Command response</AlertTitle>
                <pre style={{ margin: 0, overflow: 'auto' }}>{JSON.stringify(response, null, 2)}</pre>
              </>
            )}
          </StyledAlert>
        </Box>
      )}
      <Field label={id}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={10}>
            <TextField
              disabled={loading || isDisabled || isReadonly}
              fullWidth
              onChange={({ target }) => setConfigValue(target.value)}
              value={configValue}
            />
          </Grid>
          <Grid item xs={12} md={2} style={{ display: 'flex', alignItems: 'center' }}>
            {isReadonly ? null : (
              <Button
                variant="outlined"
                disabled={loading || isDisabled}
                fullWidth
                onClick={() => update(id, configValue)}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Update'}
              </Button>
            )}
          </Grid>
        </Grid>
      </Field>
    </ListItem>
  );
};
